import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { pubblicoFetch, ErrorePubblico } from './apiPubblica';
import { Contenitore, Intestazione, Contatti, PaginaNonDisponibile, SceltaSlot, useTitoloPagina } from './Componenti';
import PaginaStandard, { DatiPagina } from './PaginaStandard';
import ModelloPagina from './ModelloPagina';
import { formatQuandoRoma } from '../utils/dateRoma';
import { maiuscolaIniziale } from './testo';

interface Conferma { inizio: string; fine: string; sede: { nome: string; indirizzo: string }; emailInviata: boolean }

// Testo breve dell'informativa: la versione completa va fatta validare (vedi ops/percorso-acquirenti.md).
const TESTO_CONSENSO = 'Acconsento al trattamento dei miei dati da parte di Ceramita srl per organizzare gli appuntamenti e seguire la scelta delle finiture della mia casa.';

export default function PrenotaPage({ modalita }: { modalita: 'pagina' | 'acquirente' }) {
  const { token = '' } = useParams();
  const tokenUrl = encodeURIComponent(token);
  const base = modalita === 'pagina' ? `/api/pubblico/prenotazioni/${tokenUrl}` : `/api/pubblico/prenotazioni/acquirente/${tokenUrl}`;
  const caricataAt = useRef(Date.now());
  const inCorso = useRef(false);
  const [dati, setDati] = useState<DatiPagina | null>(null);
  const [nonTrovata, setNonTrovata] = useState(false);
  const [errore, setErrore] = useState('');
  const [inizio, setInizio] = useState<string | null>(null);
  const [form, setForm] = useState({ nome: '', cognome: '', email: '', telefono: '', unitaCodice: '', consensoPrivacy: false, sitoWeb: '' });
  const [invio, setInvio] = useState(false);
  const [conferma, setConferma] = useState<Conferma | null>(null);
  const [versioneSlot, setVersioneSlot] = useState(0);

  useEffect(() => {
    // @api GET /api/pubblico/prenotazioni/:token -> {"success":true,"data":"object"}
    pubblicoFetch<{ data: DatiPagina }>(base)
      .then(r => setDati(r.data))
      .catch(e => ((e as ErrorePubblico).status === 404 ? setNonTrovata(true) : setErrore((e as ErrorePubblico).message)));
  }, [base]);

  useTitoloPagina(dati ? (dati.sede ? `Prenota — ${dati.sede.nome}` : 'Prenota') : null);

  const campo = (k: 'nome' | 'cognome' | 'email' | 'telefono' | 'unitaCodice' | 'sitoWeb') =>
    (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [k]: e.target.value }));

  // Scegliendo un nuovo orario l'errore precedente (es. "orario occupato") non vale più.
  const scegliOrario = (nuovoInizio: string) => {
    setInizio(nuovoInizio);
    setErrore('');
  };

  const invia = async (e: React.FormEvent) => {
    e.preventDefault();
    // Il ref blocca anche un secondo invio che arrivasse prima del nuovo render (doppio tocco, Invio ripetuto).
    if (inCorso.current) return;
    if (!inizio) {
      setErrore('Scegli giorno e ora');
      return;
    }
    inCorso.current = true;
    setInvio(true);
    setErrore('');
    try {
      const compilatoInMs = Date.now() - caricataAt.current;
      const corpo = modalita === 'pagina'
        ? { ...form, inizio, compilatoInMs }
        : { inizio, compilatoInMs, sitoWeb: form.sitoWeb };
      // @api POST /api/pubblico/prenotazioni/:token/prenota {"inizio":"string"} -> {"success":true,"data":"object"}
      const r = await pubblicoFetch<{ data: Conferma }>(`${base}/prenota`, { method: 'POST', body: JSON.stringify(corpo) });
      setConferma(r.data);
    } catch (err) {
      const ep = err as ErrorePubblico;
      setErrore(ep.message);
      if (ep.codice === 'SLOT_OCCUPATO') {
        setInizio(null);
        setVersioneSlot(v => v + 1);
      }
    } finally {
      inCorso.current = false;
      setInvio(false);
    }
  };

  if (nonTrovata) return <PaginaNonDisponibile />;
  if (!dati) return <Contenitore><p className="bop-pubblico__testo" role={errore ? 'alert' : undefined}>{errore || 'Caricamento…'}</p></Contenitore>;

  // Aspetto del cantiere: il tema vale sempre, il modello solo a pagina aperta (la risposta può non averlo).
  const tema = dati.aspetto?.tema ?? null;
  const modello = dati.aspetto?.modello ?? null;

  if (dati.stato === 'CHIUSA') {
    return (
      <Contenitore tema={tema}>
        <Intestazione sede={dati.sede} tokenPagina={dati.tokenPagina} logoImpresa={tema?.logoImpresa} nomeImpresa={dati.cantiere?.impresa} />
        <div className="bop-pubblico__card">
          <h1 className="bop-pubblico__titolo">Prenotazioni online chiuse</h1>
          <p className="bop-pubblico__testo">Le prenotazioni online per questo cantiere non sono attive. Contatta il negozio per fissare un appuntamento.</p>
          <Contatti sede={dati.sede} />
        </div>
      </Contenitore>
    );
  }

  const cardConferma = conferma && (
    <div className="bop-pubblico__card">
      <h1 className="bop-pubblico__titolo">Appuntamento confermato</h1>
      <p className="bop-pubblico__successo" aria-live="polite">{maiuscolaIniziale(formatQuandoRoma(conferma.inizio))} presso {conferma.sede.nome}{conferma.sede.indirizzo ? `, ${conferma.sede.indirizzo}` : ''}.</p>
      <p className="bop-pubblico__testo">
        {conferma.emailInviata
          ? "Ti abbiamo inviato un'email con il riepilogo, il file per il calendario e il link per spostare o annullare l'appuntamento."
          : 'Annota data e ora: se devi spostare l\'appuntamento, contatta il negozio.'}
      </p>
      <Contatti sede={dati.sede} />
    </div>
  );

  // Il blocco di prenotazione è sempre quello standard di BOP: la pagina standard lo mette in fondo, il modello nel suo slot.
  const prenotazione = cardConferma || (
    <>
      <SceltaSlot percorsoSlot={`${base}/slot`} valore={inizio} onScegli={scegliOrario} versione={versioneSlot} />

      <form className="bop-pubblico__card" onSubmit={invia}>
        {modalita === 'pagina' && (
          <>
            <h2 className="bop-pubblico__titolo">I tuoi dati</h2>
            <div className="bop-pubblico__campi">
              <div className="bop-form-group"><label className="bop-label" htmlFor="pr-nome">Nome</label><input id="pr-nome" className="bop-input" value={form.nome} onChange={campo('nome')} required autoComplete="given-name" /></div>
              <div className="bop-form-group"><label className="bop-label" htmlFor="pr-cognome">Cognome</label><input id="pr-cognome" className="bop-input" value={form.cognome} onChange={campo('cognome')} required autoComplete="family-name" /></div>
              <div className="bop-form-group"><label className="bop-label" htmlFor="pr-email">Email</label><input id="pr-email" className="bop-input" type="email" value={form.email} onChange={campo('email')} required autoComplete="email" /></div>
              <div className="bop-form-group"><label className="bop-label" htmlFor="pr-tel">Telefono</label><input id="pr-tel" className="bop-input" type="tel" value={form.telefono} onChange={campo('telefono')} required autoComplete="tel" /></div>
              <div className="bop-form-group"><label className="bop-label" htmlFor="pr-unita">Appartamento / unità</label><input id="pr-unita" className="bop-input" value={form.unitaCodice} onChange={campo('unitaCodice')} required placeholder="es. A3, scala B int. 5" /></div>
            </div>
            <label className="bop-pubblico__consenso">
              <input type="checkbox" checked={form.consensoPrivacy} onChange={e => setForm(f => ({ ...f, consensoPrivacy: e.target.checked }))} required />
              <span className="bop-pubblico__testo">{TESTO_CONSENSO}</span>
            </label>
          </>
        )}
        <div className="bop-pubblico__trappola" aria-hidden="true">
          <label className="bop-label" htmlFor="pr-sito">Sito web</label>
          <input id="pr-sito" className="bop-input" tabIndex={-1} autoComplete="off" value={form.sitoWeb} onChange={campo('sitoWeb')} />
        </div>
        {inizio && <p className="bop-pubblico__testo">Hai scelto: {formatQuandoRoma(inizio)}</p>}
        {errore && <p className="bop-pubblico__errore" role="alert">{errore}</p>}
        <div className="bop-pubblico__azioni">
          <button type="submit" className="bop-btn bop-btn-primary" disabled={invio || !inizio}>{invio ? 'Invio…' : 'Conferma appuntamento'}</button>
        </div>
        <Contatti sede={dati.sede} />
      </form>
    </>
  );

  // Con la conferma la pagina standard mostra solo intestazione e conferma, come prima dei modelli.
  const standard = conferma ? (
    <Contenitore tema={tema}>
      <Intestazione sede={dati.sede} tokenPagina={dati.tokenPagina} logoImpresa={tema?.logoImpresa} nomeImpresa={dati.cantiere?.impresa} />
      {prenotazione}
    </Contenitore>
  ) : (
    <PaginaStandard dati={dati} modalita={modalita} tema={tema} prenotazione={prenotazione} />
  );

  return modello ? <ModelloPagina modello={modello} tema={tema} prenotazione={prenotazione} standard={standard} /> : standard;
}
